import React, { useEffect, useState } from 'react'
import { Card, CardBody, CardHeader } from '../../../../_metronic/_partials/controls'
import {
  Button,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Radio,
  RadioGroup,
  FormLabel,
  MuiThemeProvider,
  createMuiTheme,
} from '@material-ui/core'
import { useHistory, useParams } from 'react-router-dom'
import {
  cancelScheduleCampaign,
  deleteCampaign,
  getCampaignById,
  postCampaign,
  putCampaign,
  sendCampaign,
} from '../../../../api/push'
import { useSkeleton } from '../../../hooks/useSkeleton'
import { alertError, alertSuccess } from '../../../../utils/logger'
import ConfirmDialog from '../../../components/dialogs/ConfirmDialog'

const theme = createMuiTheme({
  palette: { secondary: { main: '#F64E60' } },
})

function toDatetimeLocalValue(iso) {
  if (!iso) return ''
  const d = new Date(iso)
  if (Number.isNaN(d.getTime())) return ''
  const pad = (n) => String(n).padStart(2, '0')
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`
}

function fromDatetimeLocalValue(local) {
  if (!local) return null
  const d = new Date(local)
  if (Number.isNaN(d.getTime())) return null
  return d.toISOString()
}

function getEmpty() {
  return {
    title: '',
    body: '',
    audience: 'todos',
    deliveryMode: 'draft',
    scheduledAtLocal: '',
    status: null,
    sentAt: null,
    stats: null,
  }
}

function deliveryModeFromCampaign(c) {
  if (c.status === 'scheduled' || c.scheduledAt) return 'schedule'
  return 'draft'
}

export default function EditPushPage() {
  const [campaign, setCampaign] = useState(getEmpty())
  const [openConfirmDialog, setOpenConfirmDialog] = useState(null)
  const campaignId = useParams().id
  const history = useHistory()
  const { isLoading: isLoadingData, disableLoading: disableLoadingData, ContentSkeleton } = useSkeleton()

  useEffect(() => {
    if (!campaignId) {
      disableLoadingData()
      return
    }
    getCampaignById(campaignId)
      .then((res) => {
        if (res.status === 200) {
          const c = res.data
          setCampaign({
            title: c.title || '',
            body: c.body || '',
            audience: c.audience || 'todos',
            deliveryMode: deliveryModeFromCampaign(c),
            scheduledAtLocal: toDatetimeLocalValue(c.scheduledAt),
            status: c.status,
            sentAt: c.sentAt,
            stats: c.stats,
            scheduledAt: c.scheduledAt,
          })
          disableLoadingData()
        }
      })
      .catch((error) => {
        alertError({ error, customMessage: 'No se pudo cargar la campaña.' })
        history.push('/push')
      })
  }, [campaignId, disableLoadingData, history])

  const isReadOnly = campaign.status === 'sent' || campaign.status === 'failed' || campaign.status === 'sending'
  const isEditable = !campaignId || campaign.status === 'draft' || campaign.status === 'scheduled'

  function buildPayload() {
    const payload = {
      title: campaign.title,
      body: campaign.body,
      audience: campaign.audience,
    }
    if (campaign.deliveryMode === 'now') {
      payload.sendNow = true
    } else if (campaign.deliveryMode === 'schedule') {
      const iso = fromDatetimeLocalValue(campaign.scheduledAtLocal)
      if (!iso) {
        alertError({ error: null, customMessage: 'Indica una fecha y hora de envío válidas.' })
        return null
      }
      if (new Date(iso).getTime() <= Date.now()) {
        alertError({ error: null, customMessage: 'La fecha programada debe ser futura.' })
        return null
      }
      payload.scheduledAt = iso
    }
    return payload
  }

  function save() {
    if (!campaign.title || !campaign.body) {
      alertError({ error: null, customMessage: 'Título y cuerpo son obligatorios.' })
      return
    }

    if (campaignId) {
      if (isReadOnly) {
        history.push('/push')
        return
      }
      if (campaign.deliveryMode === 'now') {
        setOpenConfirmDialog(3)
        return
      }
      const payload = {
        title: campaign.title,
        body: campaign.body,
        audience: campaign.audience,
      }
      if (campaign.deliveryMode === 'schedule') {
        const iso = fromDatetimeLocalValue(campaign.scheduledAtLocal)
        if (!iso) {
          alertError({ error: null, customMessage: 'Indica una fecha y hora de envío válidas.' })
          return
        }
        if (new Date(iso).getTime() <= Date.now()) {
          alertError({ error: null, customMessage: 'La fecha programada debe ser futura.' })
          return
        }
        payload.scheduledAt = iso
      } else {
        payload.scheduledAt = null
      }
      putCampaign(campaignId, payload)
        .then((res) => {
          if (res.status === 200) {
            alertSuccess({ title: 'Guardada', customMessage: 'Campaña actualizada.' })
            history.push('/push')
          }
        })
        .catch((error) => {
          alertError({ error, customMessage: 'No se pudo guardar.' })
        })
      return
    }

    const payload = buildPayload()
    if (!payload) return

    postCampaign(payload)
      .then((res) => {
        if (res.status === 201) {
          let msg = 'Campaña creada.'
          if (campaign.deliveryMode === 'now') msg = 'Campaña creada y enviada.'
          if (campaign.deliveryMode === 'schedule') msg = 'Campaña programada.'
          alertSuccess({ title: 'Guardada', customMessage: msg })
          history.push('/push')
        }
      })
      .catch((error) => {
        alertError({ error, customMessage: 'No se pudo guardar.' })
      })
  }

  if (isLoadingData) return <ContentSkeleton />

  return (
    <>
      <Card>
        <CardHeader title={campaignId ? 'Campaña push' : 'Nueva campaña push'} />
        <CardBody>
          <TextField
            label="Título"
            value={campaign.title || ''}
            onChange={(e) => setCampaign({ ...campaign, title: e.target.value })}
            margin="normal"
            variant="outlined"
            fullWidth
            required
            disabled={isReadOnly}
          />
          <TextField
            label="Cuerpo"
            value={campaign.body || ''}
            onChange={(e) => setCampaign({ ...campaign, body: e.target.value })}
            margin="normal"
            variant="outlined"
            fullWidth
            multiline
            rows={4}
            required
            disabled={isReadOnly}
          />
          <FormControl variant="outlined" margin="normal" fullWidth disabled={isReadOnly}>
            <InputLabel>Audiencia</InputLabel>
            <Select
              value={campaign.audience || 'todos'}
              onChange={(e) => setCampaign({ ...campaign, audience: e.target.value })}
              label="Audiencia"
            >
              <MenuItem value="todos">Todos</MenuItem>
              <MenuItem value="turistas">Turistas</MenuItem>
              <MenuItem value="residentes">Ciudadanos</MenuItem>
            </Select>
          </FormControl>

          {isEditable && (
            <FormControl component="fieldset" margin="normal" fullWidth>
              <FormLabel component="legend">Entrega</FormLabel>
              <RadioGroup
                value={campaign.deliveryMode}
                onChange={(e) => setCampaign({ ...campaign, deliveryMode: e.target.value })}
              >
                <FormControlLabel value="draft" control={<Radio color="primary" />} label="Guardar borrador" />
                <FormControlLabel value="now" control={<Radio color="primary" />} label="Enviar ahora" />
                <FormControlLabel value="schedule" control={<Radio color="primary" />} label="Programar" />
              </RadioGroup>
            </FormControl>
          )}

          {isEditable && campaign.deliveryMode === 'schedule' && (
            <TextField
              label="Fecha y hora de envío"
              type="datetime-local"
              value={campaign.scheduledAtLocal || ''}
              onChange={(e) => setCampaign({ ...campaign, scheduledAtLocal: e.target.value })}
              margin="normal"
              variant="outlined"
              fullWidth
              required
              InputLabelProps={{ shrink: true }}
            />
          )}

          {campaign.status === 'scheduled' && campaign.scheduledAt && campaign.deliveryMode !== 'schedule' && (
            <p>Programada para {new Date(campaign.scheduledAt).toLocaleString()}</p>
          )}

          {(campaign.status === 'sent' || campaign.status === 'failed') && (
            <p>
              {campaign.status === 'sent' ? 'Enviada' : 'Fallida'} el{' '}
              {campaign.sentAt ? new Date(campaign.sentAt).toLocaleString() : '—'} —{' '}
              {campaign.stats?.message || campaign.status}
            </p>
          )}
        </CardBody>
      </Card>
      <Button onClick={() => history.push('/push')} variant="outlined" style={{ marginRight: 20 }}>
        Volver
      </Button>
      {isEditable && (
        <Button onClick={save} variant="outlined" color="primary" style={{ marginRight: 20 }}>
          {campaign.deliveryMode === 'now' ? (campaignId ? 'Enviar' : 'Crear y enviar') : 'Guardar'}
        </Button>
      )}
      {campaignId && campaign.status === 'scheduled' && (
        <Button
          onClick={() => setOpenConfirmDialog(4)}
          variant="outlined"
          color="primary"
          style={{ marginRight: 20 }}
        >
          Cancelar programación
        </Button>
      )}
      {campaignId && (
        <>
          <MuiThemeProvider theme={theme}>
            <Button onClick={() => setOpenConfirmDialog(2)} variant="outlined" color="secondary">
              Eliminar
            </Button>
          </MuiThemeProvider>
          <ConfirmDialog
            title="¿Eliminar esta campaña?"
            open={openConfirmDialog === 2}
            setOpen={setOpenConfirmDialog}
            onConfirm={() => {
              deleteCampaign(campaignId)
                .then((res) => {
                  if (res.status === 200 || res.status === 204) {
                    alertSuccess({ title: 'Eliminada', customMessage: 'Campaña eliminada.' })
                    history.push('/push')
                  }
                })
                .catch((error) => {
                  alertError({ error, customMessage: 'No se pudo eliminar.' })
                })
            }}
          />
          <ConfirmDialog
            title="¿Enviar esta notificación ahora?"
            open={openConfirmDialog === 3}
            setOpen={setOpenConfirmDialog}
            onConfirm={() => {
              putCampaign(campaignId, {
                title: campaign.title,
                body: campaign.body,
                audience: campaign.audience,
              })
                .then(() => sendCampaign(campaignId))
                .then((res) => {
                  if (res.status === 200) {
                    alertSuccess({ title: 'Enviada', customMessage: 'Campaña enviada.' })
                    history.push('/push')
                  }
                })
                .catch((error) => {
                  alertError({ error, customMessage: 'No se pudo enviar.' })
                })
            }}
          />
          <ConfirmDialog
            title="¿Cancelar la programación? La campaña pasará a borrador."
            open={openConfirmDialog === 4}
            setOpen={setOpenConfirmDialog}
            onConfirm={() => {
              cancelScheduleCampaign(campaignId)
                .then((res) => {
                  if (res.status === 200) {
                    alertSuccess({ title: 'Cancelada', customMessage: 'Programación cancelada.' })
                    history.push('/push')
                  }
                })
                .catch((error) => {
                  alertError({ error, customMessage: 'No se pudo cancelar.' })
                })
            }}
          />
        </>
      )}
    </>
  )
}
